import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  useTheme,
} from "@mui/material";
import { FC } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import {
  DURATION_MAX_LENGTH,
  DURATION_STEP_MINUTES,
  MAX_DURATION_MINUTES,
  MIN_DURATION_MINUTES,
} from "./constants";
import { clampDuration, toNumberOrNull } from "./utils";

interface MinSessionDurationFieldProps {
  draft: string;
  onDraftChange: (draft: string) => void;
  /** Fired on Enter so the value can be applied without reaching for a button */
  onSubmit?: () => void;
  /** Larger touch targets and type for the mobile dialog */
  spacious?: boolean;
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
}

export const MinSessionDurationField: FC<MinSessionDurationFieldProps> = ({
  draft,
  onDraftChange,
  onSubmit,
  spacious = false,
  min = MIN_DURATION_MINUTES,
  max = MAX_DURATION_MINUTES,
  step = DURATION_STEP_MINUTES,
  maxLength = DURATION_MAX_LENGTH,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      onDraftChange("");

      return;
    }

    if (!/^\d+$/.test(inputValue)) {
      return;
    }

    if (Number.parseInt(inputValue, 10) > max) {
      return;
    }

    onDraftChange(inputValue);
  };

  // An empty field means "no minimum", so the first step lands on `min`
  const handleStep = (delta: number) => {
    const current = toNumberOrNull(draft);

    onDraftChange(
      current === null
        ? String(min)
        : String(clampDuration(current + delta, min, max)),
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent decimal point, minus sign, plus sign, 'e' (exponential)
    if (
      event.key === "." ||
      event.key === "-" ||
      event.key === "+" ||
      event.key === "e" ||
      event.key === "E"
    ) {
      event.preventDefault();

      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit?.();

      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      handleStep(step);

      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      handleStep(-step);
    }
  };

  const draftNumber = toNumberOrNull(draft);
  const isEmpty = draft === "";
  const canDecrease = draftNumber !== null && draftNumber > min;
  const canIncrease = draftNumber === null || draftNumber < max;

  const fieldHeight = spacious ? 56 : 32;
  const stepperSize = spacious ? 40 : 24;
  const iconSize = spacious ? 22 : 16;
  const fontSize = spacious ? 20 : 14;
  const placeholderFontSize = spacious ? 15 : 11;
  const unitFontSize = spacious ? 13 : 10;
  const anyLabel = t("components.minSessionDurationFilter.any");
  const valueWidth = `calc(${isEmpty ? anyLabel.length : draft.length}ch + 2px)`;

  const stepperSx = {
    height: stepperSize,
    width: stepperSize,
    borderRadius: spacious ? "12px" : "8px",
    color: theme.colors.text[5],
    "&:hover": {
      backgroundColor: theme.colors.secondary[1],
    },
    "&.Mui-disabled": {
      color: theme.colors.secondary[3],
    },
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        backgroundColor: theme.colors.grey,
        borderRadius: spacious ? "14px" : "16px",
        display: "flex",
        flex: spacious ? 1 : "0 0 auto",
        height: fieldHeight,
        justifyContent: "space-between",
        paddingLeft: spacious ? "8px" : "4px",
        paddingRight: spacious ? "8px" : "4px",
        width: spacious ? undefined : 128,
      }}
    >
      <IconButton
        aria-label={t("components.minSessionDurationFilter.decrease")}
        disabled={!canDecrease}
        onClick={() => handleStep(-step)}
        sx={stepperSx}
      >
        <Remove sx={{ fontSize: iconSize }} />
      </IconButton>
      <Box
        sx={{
          alignItems: "flex-end",
          display: "flex",
          gap: 0.5,
        }}
      >
        <InputBase
          inputProps={{
            "aria-label": t("components.minSessionDurationFilter.label"),
            inputMode: "numeric",
            maxLength,
            pattern: "[0-9]*",
            type: "text",
          }}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={anyLabel}
          sx={{
            color: theme.colors.text[6],
            fontSize,
            fontWeight: 500,
            lineHeight: 1,
            width: valueWidth,
            "& .MuiInputBase-input": {
              height: `${fontSize}px`,
              lineHeight: 1,
              p: 0,
              textAlign: "center",
              "&::placeholder": {
                color: theme.colors.text[0],
                fontSize: placeholderFontSize,
                opacity: 1,
              },
            },
          }}
          value={draft}
        />
        {!isEmpty && (
          <Typography
            aria-hidden={isEmpty}
            sx={{
              color: theme.colors.text[1],
              fontSize: unitFontSize,
              fontWeight: 500,
              lineHeight: 1,
            }}
          >
            {t("components.minSessionDurationFilter.unit")}
          </Typography>
        )}
      </Box>
      <IconButton
        aria-label={t("components.minSessionDurationFilter.increase")}
        disabled={!canIncrease}
        onClick={() => handleStep(step)}
        sx={stepperSx}
      >
        <Add sx={{ fontSize: iconSize }} />
      </IconButton>
    </Box>
  );
};
